import * as React from 'react';
import { CustomButton, ButtonType, SpinnerWrapper, AlertMessage, AlertType } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { IEmergencyTreatmentsModel } from '../../../models/IEmergencyTreatment';
import { EmergencyTreatmentApi } from '../../../api/EmergencyTreatmentApi';
import { EmergencyTreatmentsTable } from './EmergencyTreatmentsTable';
import { EmergencyTreatmentsModal } from './EmergencyTreatmentsModal';
import { setMessage } from '../../../helpers/MessageHelper';
import { IPatientTreatment } from '../../../models/IPatientTreatment';

interface IEmergencyTreatments {
    emergencyTreatmentReportId?: number;
    canAddPatientTreatment: boolean;
    reportedTreatments: IEmergencyTreatmentsModel | undefined;
    updateReportedTreatments: (treatments: IEmergencyTreatmentsModel) => void;
}

export const EmergencyTreatments: React.FunctionComponent<IEmergencyTreatments> = ({ emergencyTreatmentReportId, canAddPatientTreatment, updateReportedTreatments, reportedTreatments }) => {
    const auth = useAuth();
    const api = new EmergencyTreatmentApi(auth);

    const [isApiFetching, setIsApiFetching] = React.useState<boolean>(false);
    const [isValidTreatmentsFetching, setIsValidTreatmentsFetching] = React.useState<boolean>(false);
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [validTreatmentCodes, setValidTreatmentCodes] = React.useState<IPatientTreatment[]>([]);
    const [renderModalForm, setRenderModalForm] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (emergencyTreatmentReportId) {
            getReportedTreatments();
        }
    }, [emergencyTreatmentReportId])

    const getReportedTreatments = () => {
        if (emergencyTreatmentReportId) {
            setIsApiFetching(true);
            api.getEmergencyTreatments(emergencyTreatmentReportId, (treatments: IEmergencyTreatmentsModel) => {
                updateReportedTreatments(treatments);
            }, (message) => {
                const newMessage = setMessage(message, errorMessage ?? "");
                setErrorMessage(newMessage);
            }, () => {
                setIsApiFetching(false);
            });
        }
    }

    const onTreatmentDelete = (patientTreatmentId: number) => {
        api.deleteEmergencyTreatment(patientTreatmentId, () => {
            const newMessage = setMessage("Åtgärdskoden har tagits bort", successMessage ?? "");
            getReportedTreatments();
            setSuccessMessage(newMessage);
        }, (message) => {
            const newMessage = setMessage(message, errorMessage ?? "");
            setErrorMessage(newMessage);
        })
    }

    const onTreatmentCredit = (patientTreatmentId: number) => {
        api.creditTreatment(patientTreatmentId, () => {
            const newMessage = setMessage("Åtgärdskoden inväntar kreditering", successMessage ?? "");
            getReportedTreatments();
            setSuccessMessage(newMessage);
        });
    }

    const onTreatmentSubmitSuccess = () => {
        const message = setMessage("Åtgärdskoden har lagts till", successMessage ?? "");
        setSuccessMessage(message);
        getReportedTreatments();
    }

    const getValidTreatmentCodes = () => {
        if (emergencyTreatmentReportId) {
            setIsValidTreatmentsFetching(true)
            api.getValidTreatmentCodes(emergencyTreatmentReportId, (codes: IPatientTreatment[]) => {
                setValidTreatmentCodes(codes);
            }, (message) => {
                const newMessage = setMessage(message, errorMessage ?? "");
                setErrorMessage(newMessage);
            }, () => { setIsValidTreatmentsFetching(false) });
        }
    }

    const openTreatmentModal = () => {
        getValidTreatmentCodes();
        setRenderModalForm(true);
    }

    return (
        <>
            {
                emergencyTreatmentReportId && (canAddPatientTreatment || (reportedTreatments?.treatments && reportedTreatments?.treatments.length > 0)) ?
                    <SpinnerWrapper isLoading={isApiFetching}>
                        <div className="row">
                            <h5 className="col-12">Åtgärdskoder</h5>
                        </div>
                        <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                        <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                        <section>
                            {
                                canAddPatientTreatment ?
                                    <div className="panel text-right">
                                        <CustomButton id="emergency-treatment-add-treatment" buttonText="Lägg till åtgärdskod" buttonType={ButtonType.create} onClick={openTreatmentModal} />
                                    </div>
                                    : null
                            }
                            {
                                reportedTreatments ?
                                    <EmergencyTreatmentsTable
                                        registeredTreatments={reportedTreatments}
                                        canAddPatientTreatment={canAddPatientTreatment}
                                        onTreatmentDelete={onTreatmentDelete}
                                        onTreatmentCredit={onTreatmentCredit}
                                    />
                                    : null
                            }
                            {
                                canAddPatientTreatment ?
                                    <EmergencyTreatmentsModal
                                        emergencyTreatmentReportId={emergencyTreatmentReportId}
                                        validTreatmentCodes={validTreatmentCodes}
                                        onSubmitSuccess={onTreatmentSubmitSuccess}
                                        onClose={() => setRenderModalForm(false)}
                                        renderForm={renderModalForm}
                                        isFetching={isValidTreatmentsFetching} />
                                    : null
                            }
                        </section>
                    </SpinnerWrapper>
                    : null
            }
        </>
    );
}